import React, { Fragment, FunctionComponent, useState } from 'react';
import { Field } from 'react-final-form';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import {
  composeValidators,
  digitsOnly,
  emailValidation,
  fileValidation,
  requiredValidation,
  onlyLetters,
  pastDateOnly,
  validateIdCard,
  validateIdCardWithSelection,
} from 'helpers/validations';
import { getDocLabel } from 'helpers/form';
import FieldRequired from 'components/commons/FieldRequired';

import MultiStepForm from 'components/commons/MultiStepForm';
import Input from 'components/inputs/Input';
import SelectInput from 'components/inputs/SelectInput';
import Checkbox from 'components/inputs/Checkbox';
import LocationSelector from 'components/commons/LocationSelector';
import ProcedureStartStep from 'components/commons/ProcedureStartStep';
import ProcedureReviewStep from 'components/commons/ProcedureReviewStep';
import { IReviewSection } from 'components/commons/ProcedureReviewStep/types';

import { ViewContainer, ViewContent, Title } from 'styles/view';
import { FormRowContainer } from 'styles/form';

import { IMarriagesFormData, IMarriagesFormProps } from './types';
import './i18n';

const MarriagesForm: FunctionComponent<IMarriagesFormProps> = (props: IMarriagesFormProps) => {
  const {
    cementeries,
    civilStates,
    files,
    idCardTypes,
    initialValues,
    isEditing,
    onSubmit,
    tribes,
  } = props;
  const [formValues, setFormValues] = useState<IMarriagesFormData>(initialValues);
  const [showStartStep, setShowStartStep] = useState<boolean>(!isEditing);

  const tabs = [
    i18next.t<string>('MARRIAGES_FORM:GROOM'),
    i18next.t<string>('MARRIAGES_FORM:BRIDE'),
    i18next.t<string>('MARRIAGES_FORM:PARENTS'),
    i18next.t<string>('MARRIAGES_FORM:GRANDPARENTS'),
    i18next.t<string>('MARRIAGES_FORM:WITNESSES'),
    i18next.t<string>('MARRIAGES_FORM:CEREMONY'),
    i18next.t<string>('MARRIAGES_FORM:COMMENTS'),
  ];

  const today = new Date().toISOString().split('T')[0];

  const requiredFilesDescription = [
    // PESTAÑA: NOVIOS
    `[OBLIGATORIO] ${i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE')}: ${i18next.t<string>(
      'PROCEDURES:ID_COUPLE_DESC',
      'DNI Frente y Dorso de ambos novios.',
    )}`,

    // PESTAÑA: PADRES (Usan FieldRequired en tu código)
    `[OBLIGATORIO] ${i18next.t<string>('MARRIAGES_FORM:PARENTS_ID_FILES')}: ${i18next.t<string>(
      'PROCEDURES:ID_PARENTS_DESC',
      'DNI Frente y Dorso de los padres de ambos novios.',
    )}`,
    `[OBLIGATORIO] ${i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}: ${i18next.t<string>(
      'PROCEDURES:KETUBA_PARENTS_DESC',
      'Libreta de matrimonio religioso de los padres.',
    )}`,
    `[OBLIGATORIO] ${i18next.t<string>(
      'MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE',
    )}: ${i18next.t<string>(
      'PROCEDURES:CERT_PARENTS_DESC',
      'Acta de matrimonio civil de los padres.',
    )}`,

    // PESTAÑA: ABUELOS (Usan Field en tu código, son opcionales o según disponibilidad)
    `[OPCIONAL] ${i18next.t<string>('MARRIAGES_FORM:GRANDPARENTS_LINEAGE')}: ${i18next.t<string>(
      'PROCEDURES:KETUBA_GRAND_DESC',
      'Ketubá o Actas de matrimonio civil de los abuelos para acreditar linaje.',
    )}`,

    // CASOS ESPECIALES Y TESTIGOS
    `[OPCIONAL] ${i18next.t<string>('MARRIAGES_FORM:GET_FILE')}: ${i18next.t<string>(
      'PROCEDURES:GET_DESC',
      'Acta de divorcio religioso (GET) si alguno es divorciado.',
    )}`,
    `[OPCIONAL] ${i18next.t<string>('MARRIAGES_FORM:WITNESS_DOCS')}: ${i18next.t<string>(
      'PROCEDURES:WITNESS_DESC',
      'DNI y datos de contacto de los testigos designados.',
    )}`,
  ];

  const reviewSections: IReviewSection[] = [
    // 1. Resumen de Novio y Novia (Groom & Bride)
    ...(['groom', 'bride'] as const).map((side) => ({
      title: i18next.t(`MARRIAGES_FORM:${side.toUpperCase()}_INFORMATION`),
      fields: [
        { label: i18next.t('MARRIAGES_FORM:LAST_NAME'), value: formValues[side]?.lastName },
        { label: i18next.t('MARRIAGES_FORM:FIRST_NAME'), value: formValues[side]?.firstName },
        { label: i18next.t('MARRIAGES_FORM:ID_CARD'), value: formValues[side]?.idCard },
        { label: i18next.t('MARRIAGES_FORM:ID_CARD_TYPE'), value: formValues[side]?.idCardType },
        { label: i18next.t('MARRIAGES_FORM:CIVIL_STATE'), value: formValues[side]?.civilState },
        { label: i18next.t('MARRIAGES_FORM:TRIBE'), value: formValues[side]?.tribe },
        { label: i18next.t('MARRIAGES_FORM:ADDRESS'), value: formValues[side]?.address },
        { label: i18next.t('MARRIAGES_FORM:APARTMENT'), value: formValues[side]?.apartment },
        { label: i18next.t('MARRIAGES_FORM:BIRTHPLACE'), value: formValues[side]?.birthplace },
        { label: i18next.t('MARRIAGES_FORM:BIRTHDATE'), value: formValues[side]?.birthdate },
        { label: i18next.t('MARRIAGES_FORM:EMAIL'), value: formValues[side]?.email },
        {
          label: i18next.t('MARRIAGES_FORM:PHONE'),
          value: formValues[side]?.phone
            ? `${formValues[side]?.areaCode || ''} ${formValues[side]?.phone}`
            : null,
        },
        { label: i18next.t('MARRIAGES_FORM:COMMUNITY'), value: formValues[side]?.community },
        { label: i18next.t('MARRIAGES_FORM:JOB'), value: formValues[side]?.job },
      ],
    })),

    // 2. Resumen de Padres de ambos
    ...(['groom', 'bride'] as const).map((side) => ({
      title: i18next.t(`MARRIAGES_FORM:${side.toUpperCase()}_PARENTS_INFORMATION`),
      fields: [
        {
          label: `Padre: ${i18next.t('MARRIAGES_FORM:FATHER_LAST_NAME')}`,
          value: formValues[side]?.parents?.father?.lastName,
        },
        {
          label: `Padre: ${i18next.t('MARRIAGES_FORM:FATHER_NAME')}`,
          value: formValues[side]?.parents?.father?.name,
        },
        {
          label: `Padre: ${i18next.t('MARRIAGES_FORM:ID_CARD')}`,
          value: formValues[side]?.parents?.father?.idCard,
        },
        {
          label: `Madre: ${i18next.t('MARRIAGES_FORM:MOTHER_LAST_NAME')}`,
          value: formValues[side]?.parents?.mother?.lastName,
        },
        {
          label: `Madre: ${i18next.t('MARRIAGES_FORM:MOTHER_NAME')}`,
          value: formValues[side]?.parents?.mother?.name,
        },
        {
          label: `Madre: ${i18next.t('MARRIAGES_FORM:ID_CARD')}`,
          value: formValues[side]?.parents?.mother?.idCard,
        },
        {
          label: i18next.t('MARRIAGES_FORM:WEDDING_TEMPLE'),
          value: formValues[side]?.parents?.weddingTemple,
        },
        {
          label: i18next.t('MARRIAGES_FORM:WEDDING_DATE'),
          value: formValues[side]?.parents?.weddingDate,
        },
      ],
    })),

    // 3. Resumen de Abuelos (Linaje)
    ...(['groom', 'bride'] as const).map((side) => ({
      title: i18next.t(`MARRIAGES_FORM:${side.toUpperCase()}_GRANDPARENTS_INFORMATION`),
      fields: [
        {
          label: `Paterno: ${i18next.t('MARRIAGES_FORM:PATERNAL_FATHER_NAME')}`,
          value: formValues[side]?.grandParents?.paternalFather?.name,
        },
        {
          label: `Materno: ${i18next.t('MARRIAGES_FORM:PATERNAL_MOTHER_NAME')}`,
          value: formValues[side]?.grandParents?.paternalMother?.name,
        },
        {
          label: `Paterno: ${i18next.t('MARRIAGES_FORM:MATERNAL_FATHER_NAME')}`,
          value: formValues[side]?.grandParents?.maternalFather?.name,
        },
        {
          label: `Materno: ${i18next.t('MARRIAGES_FORM:MATERNAL_MOTHER_NAME')}`,
          value: formValues[side]?.grandParents?.maternalMother?.name,
        },
      ],
    })),

    // 4. Testigos Dinámicos
    ...(['groom', 'bride'] as const).flatMap((side) =>
      (formValues[side]?.witnesses || []).map((witness: any, index: number) => ({
        title: `${i18next.t(`MARRIAGES_FORM:${side.toUpperCase()}_WITNESSES_INFORMATION`)} ${
          index + 1
        }`,
        fields: [
          { label: i18next.t('MARRIAGES_FORM:WITNESS_NAME'), value: witness.name },
          { label: i18next.t('MARRIAGES_FORM:ID_CARD'), value: witness.idCard },
          { label: i18next.t('MARRIAGES_FORM:EMAIL'), value: witness.email },
          { label: i18next.t('MARRIAGES_FORM:ADDRESS'), value: witness.address },
          {
            label: i18next.t('MARRIAGES_FORM:PHONE'),
            value: witness.phone ? `${witness.areaCode || ''} ${witness.phone}` : null,
          },
        ],
      })),
    ),

    // 5. Detalles de la Ceremonia
    {
      title: i18next.t('MARRIAGES_FORM:CEREMONY_INFORMATION'),
      fields: [
        { label: i18next.t('MARRIAGES_FORM:ADDRESS'), value: formValues.ceremony?.address },
        { label: i18next.t('MARRIAGES_FORM:LOCALITY'), value: formValues.ceremony?.locality },
        {
          label: i18next.t('MARRIAGES_FORM:CEREMONY_RELIGIOUS_DATE'),
          value: formValues.ceremony?.religiousDate,
        },
        {
          label: i18next.t('MARRIAGES_FORM:CEREMONY_CIVIL_DATE'),
          value: formValues.ceremony?.civilDate,
        },
        {
          label: i18next.t('MARRIAGES_FORM:CEREMONY_OFFICIANT'),
          value: formValues.ceremony?.officiant,
        },
        {
          label: `Civil: ${i18next.t('MARRIAGES_FORM:COUNTRY', 'País')}`,
          value: formValues.ceremony?.civilLocation?.country,
        },
        {
          label: `Civil: ${i18next.t('MARRIAGES_FORM:PROVINCE', 'Provincia')}`,
          value: formValues.ceremony?.civilLocation?.province,
        },
        {
          label: `Civil: ${i18next.t('MARRIAGES_FORM:LOCALITY', 'localidad')}`,
          value: formValues.ceremony?.civilLocation?.locality,
        },
      ],
    },

    {
      title: i18next.t('MARRIAGES_FORM:COMMENTS'),
      fields: [{ label: i18next.t('MARRIAGES_FORM:COMMENTS'), value: formValues.comments }],
    },
  ];

  if (showStartStep) {
    return (
      <ViewContainer auth>
        <ProcedureStartStep
          procedureName={i18next.t<string>('MARRIAGES_FORM:TITLE')}
          requiredFiles={requiredFilesDescription}
          onStart={() => setShowStartStep(false)}
        />
      </ViewContainer>
    );
  }

  return (
    <ViewContainer auth data-testid="marriage-form">
      <Title>{i18next.t<string>('MARRIAGES_FORM:TITLE')}</Title>
      <ViewContent auth>
        <MultiStepForm<IMarriagesFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          onValuesChange={setFormValues}
          tabs={tabs}
          tabsEnabled={isEditing}
        >
          {['groom', 'bride'].map((value) => (
            <MultiStepForm.Page key={`${value}-page`}>
              <Typography variant="h6">
                {i18next.t<string>(`MARRIAGES_FORM:${value.toUpperCase()}_INFORMATION`)}
              </Typography>
              <br />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:LAST_NAME')}
                  name={`${value}.lastName`}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:FIRST_NAME')}
                  name={`${value}.firstName`}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_TYPE')}
                  name={`${value}.idCardType`}
                  options={idCardTypes}
                  render={SelectInput}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={getDocLabel(`${value}.idCard`, formValues, idCardTypes)}
                  name={`${value}.idCard`}
                  render={Input}
                  validate={validateIdCard(idCardTypes)}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE')}
                  name={`${value}.idCardFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE'),
                      value: files?.[`${value}IdCardFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE')}
                  name={`${value}.idCardBackFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE'),
                      value: files?.[`${value}IdCardBackFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('MARRIAGES_FORM:CIVIL_STATE')}
                  name={`${value}.civilState`}
                  options={civilStates}
                  render={SelectInput}
                  validate={requiredValidation}
                />
                <Field
                  label={i18next.t<string>('MARRIAGES_FORM:TRIBE')}
                  name={`${value}.tribe`}
                  options={tribes}
                  render={SelectInput}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:ADDRESS')}
                  name={`${value}.address`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:APARTMENT')}
                  name={`${value}.apartment`}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <LocationSelector path={value} values={formValues} />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHDATE')}
                  name={`${value}.birthdate`}
                  render={Input}
                  type="date"
                  max={today}
                  validate={composeValidators(requiredValidation, pastDateOnly)}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:EMAIL')}
                  name={`${value}.email`}
                  render={Input}
                  type="email"
                  validate={composeValidators(requiredValidation, emailValidation)}
                />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:AREA_CODE')}
                    maxLength={4}
                    name={`${value}.areaCode`}
                    parse={(v: string) => digitsOnly(v)}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PHONE')}
                    maxLength={8}
                    name={`${value}.phone`}
                    parse={(v: string) => digitsOnly(v)}
                    render={Input}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:COMMUNITY')}
                  name={`${value}.community`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:JOB')}
                  name={`${value}.job`}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              {formValues[value as 'groom' | 'bride'].civilState === 'DIVORCED' && (
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:GET_FILE')}
                  name={`${value}.getFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('MARRIAGES_FORM:GET_FILE'),
                      value: files?.[`${value}GetFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              )}
            </MultiStepForm.Page>
          ))}
          <MultiStepForm.Page key="parents-page">
            {['groom', 'bride'].map((side) => (
              <Fragment key={`${side}-parents`}>
                <Typography
                  variant="h6"
                  sx={{ mt: 3, mb: 2, borderBottom: '2px solid #2563eb', pb: 1 }}
                >
                  {i18next.t<string>(`MARRIAGES_FORM:${side.toUpperCase()}_PARENTS_INFORMATION`)}
                </Typography>

                {/* --- BLOQUE EXCLUSIVO: PADRE --- */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', color: '#1e293b', mb: 2 }}
                >
                  {i18next.t<string>('MARRIAGES_FORM:FATHER_DATA')}
                </Typography>

                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:FATHER_LAST_NAME')}
                    name={`${side}.parents.father.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:FATHER_NAME')}
                    name={`${side}.parents.father.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>

                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_TYPE')}
                    name={`${side}.parents.father.idCardType`}
                    options={idCardTypes}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={getDocLabel(`${side}.parents.father.idCard`, formValues, idCardTypes)}
                    name={`${side}.parents.father.idCard`}
                    render={Input}
                    validate={validateIdCard(idCardTypes)}
                  />
                </FormRowContainer>

                {/* ARCHIVOS DE ID INMEDIATAMENTE DESPUÉS DEL NÚMERO */}
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE')}
                    name={`${side}.parents.father.idCardFile`}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE')}
                    name={`${side}.parents.father.idCardBackFile`}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                </FormRowContainer>

                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                    name={`${side}.parents.father.birthplace`}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:BIRTHDATE')}
                    name={`${side}.parents.father.birthdate`}
                    render={Input}
                    type="date"
                    max={today}
                    validate={composeValidators(requiredValidation, pastDateOnly)}
                  />
                </FormRowContainer>

                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:CIVIL_STATE')}
                    name={`${side}.parents.father.civilState`}
                    options={civilStates}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:TRIBE')}
                    name={`${side}.parents.father.tribe`}
                    options={tribes}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                </FormRowContainer>

                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:ALIVE')}
                    name={`${side}.parents.father.alive`}
                    render={Checkbox}
                  />
                  {!formValues[side as 'groom' | 'bride'].parents.father.alive && (
                    <Field
                      label={i18next.t<string>('MARRIAGES_FORM:CEMENTERY')}
                      name={`${side}.parents.father.cementery`}
                      options={cementeries}
                      render={SelectInput}
                    />
                  )}
                </FormRowContainer>

                {formValues[side as 'groom' | 'bride'].parents.father.civilState === 'DIVORCED' && (
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:GET_FILE')}
                    name={`${side}.parents.father.getFile`}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                )}

                <hr style={{ margin: '30px 0', border: '0', borderTop: '1px solid #eee' }} />

                {/* --- BLOQUE EXCLUSIVO: MADRE --- */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', color: '#1e293b', mb: 2 }}
                >
                  {i18next.t<string>('MARRIAGES_FORM:MOTHER_DATA')}
                </Typography>

                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MOTHER_LAST_NAME')}
                    name={`${side}.parents.mother.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MOTHER_NAME')}
                    name={`${side}.parents.mother.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>

                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_TYPE')}
                    name={`${side}.parents.mother.idCardType`}
                    options={idCardTypes}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={getDocLabel(`${side}.parents.mother.idCard`, formValues, idCardTypes)}
                    name={`${side}.parents.mother.idCard`}
                    render={Input}
                    validate={validateIdCard(idCardTypes)}
                  />
                </FormRowContainer>

                {/* ARCHIVOS DE ID MADRE */}
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE')}
                    name={`${side}.parents.mother.idCardFile`}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE')}
                    name={`${side}.parents.mother.idCardBackFile`}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                </FormRowContainer>

                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                    name={`${side}.parents.mother.birthplace`}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:BIRTHDATE')}
                    name={`${side}.parents.mother.birthdate`}
                    render={Input}
                    type="date"
                    max={today}
                    validate={composeValidators(requiredValidation, pastDateOnly)}
                  />
                </FormRowContainer>

                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:CIVIL_STATE')}
                    name={`${side}.parents.mother.civilState`}
                    options={civilStates}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:TRIBE')}
                    name={`${side}.parents.mother.tribe`}
                    options={tribes}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                </FormRowContainer>

                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:ALIVE')}
                    name={`${side}.parents.mother.alive`}
                    render={Checkbox}
                  />
                  {!formValues[side as 'groom' | 'bride'].parents.mother.alive && (
                    <Field
                      label={i18next.t<string>('MARRIAGES_FORM:CEMENTERY')}
                      name={`${side}.parents.mother.cementery`}
                      options={cementeries}
                      render={SelectInput}
                    />
                  )}
                </FormRowContainer>

                {formValues[side as 'groom' | 'bride'].parents.mother.civilState === 'DIVORCED' && (
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:GET_FILE')}
                    name={`${side}.parents.mother.getFile`}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                )}

                {/* --- DATOS COMUNES --- */}
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 3, mb: 2, fontStyle: 'italic', color: '#64748b' }}
                >
                  {i18next.t<string>('MARRIAGES_FORM:UNION_DOCS')}
                </Typography>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:WEDDING_TEMPLE')}
                    name={`${side}.parents.weddingTemple`}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:WEDDING_DATE')}
                    name={`${side}.parents.weddingDate`}
                    render={Input}
                    type="date"
                    max={today}
                    validate={composeValidators(requiredValidation, pastDateOnly)}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}
                    name={`${side}.parents.ketubaFile`}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}
                    name={`${side}.parents.marriageCertificateFile`}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                </FormRowContainer>
              </Fragment>
            ))}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="grand-parents-page">
            {['groom', 'bride'].map((value) => (
              <Fragment key={`${value}-grandparents`}>
                <Typography variant="h6">
                  {i18next.t<string>(
                    `MARRIAGES_FORM:${value.toUpperCase()}_GRANDPARENTS_INFORMATION`,
                  )}
                </Typography>
                <br />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PATERNAL_FATHER_LAST_NAME')}
                    name={`${value}.grandParents.paternalFather.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PATERNAL_FATHER_NAME')}
                    name={`${value}.grandParents.paternalFather.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.grandParents.paternalFather.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PATERNAL_MOTHER_LAST_NAME')}
                    name={`${value}.grandParents.paternalMother.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PATERNAL_MOTHER_NAME')}
                    name={`${value}.grandParents.paternalMother.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.grandParents.paternalMother.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}
                    name={`${value}.grandParents.paternalKetubaFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE'),
                        value: files?.[`${value}GrandParentsPaternalKetubaFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={fileValidation}
                  />
                  <Field
                    label={i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}
                    name={`${value}.grandParents.paternalMarriageCertificateFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE'),
                        value: files?.[`${value}GrandParentsPaternalMarriageCertificateFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={fileValidation}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MATERNAL_FATHER_LAST_NAME')}
                    name={`${value}.grandParents.maternalFather.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MATERNAL_FATHER_NAME')}
                    name={`${value}.grandParents.maternalFather.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.grandParents.maternalFather.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MATERNAL_MOTHER_LAST_NAME')}
                    name={`${value}.grandParents.maternalMother.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MATERNAL_MOTHER_NAME')}
                    name={`${value}.grandParents.maternalMother.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.grandParents.maternalMother.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}
                    name={`${value}.grandParents.maternalKetubaFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE'),
                        value: files?.[`${value}GrandParentsMaternalKetubaFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={fileValidation}
                  />
                  <Field
                    label={i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}
                    name={`${value}.grandParents.maternalMarriageCertificateFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE'),
                        value: files?.[`${value}GrandParentsMaternalMarriageCertificateFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={fileValidation}
                  />
                </FormRowContainer>
              </Fragment>
            ))}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="witnesses-page">
            {['groom', 'bride'].map((value) => (
              <Fragment key={`${value}-witnesses`}>
                <Typography variant="h6">
                  {i18next.t<string>(`MARRIAGES_FORM:${value.toUpperCase()}_WITNESSES_INFORMATION`)}
                </Typography>
                <br />
                {(formValues[value as 'groom' | 'bride']?.witnesses as Array<unknown>)?.map(
                  (_witness: unknown, i: number) => {
                    const currentTypeId =
                      formValues[value as 'groom' | 'bride']?.witnesses?.[i]?.idCardType;
                    return (
                      <Fragment key={`witness-${i.toString()}`}>
                        <FormRowContainer>
                          <FieldRequired
                            label={`${i18next.t<string>('MARRIAGES_FORM:WITNESS_NAME')} ${i + 1}`}
                            name={`${value}.witnesses[${i}].name`}
                            parse={(v: string) => v}
                            render={Input}
                            validate={composeValidators(requiredValidation, onlyLetters)}
                          />
                          <Field
                            label={i18next.t<string>('MARRIAGES_FORM:EMAIL')}
                            name={`${value}.witnesses[${i}].email`}
                            parse={(v: string) => v}
                            render={Input}
                            type="email"
                            validate={emailValidation}
                          />
                        </FormRowContainer>
                        <FormRowContainer>
                          <Field
                            label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_TYPE')}
                            name={`${value}.witnesses[${i}].idCardType`}
                            options={idCardTypes}
                            parse={(v) => v}
                            render={SelectInput}
                          />
                          <Field
                            label={getDocLabel(
                              `${value}.witnesses[${i}].idCard`,
                              formValues,
                              idCardTypes,
                            )}
                            name={`${value}.witnesses[${i}].idCard`}
                            parse={(v) => v}
                            render={Input}
                            validate={validateIdCardWithSelection(
                              idCardTypes,
                              currentTypeId,
                              false,
                            )}
                          />
                        </FormRowContainer>
                        <FormRowContainer>
                          <Field
                            label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE')}
                            name={`${value}.witnesses[${i}].idCardFile`}
                            previewFiles={[
                              {
                                label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE'),
                                value: files?.[`${value}WitnessesIdCardFiles`]?.[i],
                              },
                            ]}
                            render={Input}
                            type="file"
                            validate={fileValidation}
                          />
                          <Field
                            label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE')}
                            name={`${value}.witnesses[${i}].idCardBackFile`}
                            previewFiles={[
                              {
                                label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE'),
                                value: files?.[`${value}WitnessesIdCardBackFiles`]?.[i],
                              },
                            ]}
                            render={Input}
                            type="file"
                            validate={fileValidation}
                          />
                        </FormRowContainer>
                        <FormRowContainer>
                          <Field
                            label={i18next.t<string>('MARRIAGES_FORM:ADDRESS')}
                            name={`${value}.witnesses[${i}].address`}
                            parse={(v) => v}
                            render={Input}
                          />
                          <FormRowContainer>
                            <Field
                              label={i18next.t<string>('MARRIAGES_FORM:AREA_CODE')}
                              maxLength={4}
                              name={`${value}.witnesses[${i}].areaCode`}
                              parse={(v) => digitsOnly(v)}
                              render={Input}
                            />
                            <Field
                              label={i18next.t<string>('MARRIAGES_FORM:PHONE')}
                              maxLength={8}
                              name={`${value}.witnesses[${i}].phone`}
                              parse={(v) => digitsOnly(v)}
                              render={Input}
                            />
                          </FormRowContainer>
                        </FormRowContainer>
                        <FormRowContainer>
                          <Field
                            label={i18next.t<string>('MARRIAGES_FORM:KETUBA_FILE')}
                            name={`${value}.witnesses[${i}].ketubaFile`}
                            previewFiles={[
                              {
                                label: i18next.t<string>('MARRIAGES_FORM:KETUBA_FILE'),
                                value: files?.[`${value}WitnessesKetubaFiles`]?.[i],
                              },
                            ]}
                            render={Input}
                            type="file"
                            validate={fileValidation}
                          />
                          <Field
                            label={i18next.t<string>('MARRIAGES_FORM:MARRIAGE_CERTIFICATE_FILE')}
                            name={`${value}.witnesses[${i}].marriageCertificateFile`}
                            previewFiles={[
                              {
                                label: i18next.t<string>(
                                  'MARRIAGES_FORM:MARRIAGE_CERTIFICATE_FILE',
                                ),
                                value: files?.[`${value}WitnessesMarriageCertificateFiles`]?.[i],
                              },
                            ]}
                            render={Input}
                            type="file"
                            validate={fileValidation}
                          />
                        </FormRowContainer>
                      </Fragment>
                    );
                  },
                )}
              </Fragment>
            ))}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="ceremony-page">
            <Typography variant="h6">
              {i18next.t<string>('MARRIAGES_FORM:CEREMONY_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('MARRIAGES_FORM:ADDRESS')}
                name="ceremony.address"
                render={Input}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('MARRIAGES_FORM:LOCALITY')}
                name="ceremony.locality"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('MARRIAGES_FORM:CEREMONY_RELIGIOUS_DATE')}
                name="ceremony.religiousDate"
                render={Input}
                type="datetime-local"
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('MARRIAGES_FORM:CEREMONY_CIVIL_DATE')}
                name="ceremony.civilDate"
                parse={(v) => v}
                render={Input}
                type="datetime-local"
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('MARRIAGES_FORM:CEREMONY_OFFICIANT')}
              name="ceremony.officiant"
              render={Input}
              validate={requiredValidation}
            />
            <LocationSelector path="ceremony.civilLocation" values={formValues} />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="comments-page">
            <Field
              label={i18next.t<string>('MARRIAGES_FORM:COMMENTS')}
              name="comments"
              parse={(value) => value}
              render={Input}
              type="textarea"
            />
          </MultiStepForm.Page>
          {!isEditing && (
            <MultiStepForm.Page key="review-page">
              <ProcedureReviewStep sections={reviewSections} />
            </MultiStepForm.Page>
          )}
        </MultiStepForm>
      </ViewContent>
    </ViewContainer>
  );
};

export default MarriagesForm;
